import { jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { priceFormatter } from "@/lib/utils";
import { LIBRARIES_INDEX_PAGE, MYPAGE_PASSES, PASSES_ORDER } from "@/settings/constant";
import { styled } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef } from "react";
import { getMembershipAvailable, getRelatedProducts, postUseMembership, Subjects } from "src/api/product";
import useHandoutProductStore from "src/store/relatedProduct";
import { usePurchaseStore } from "../purchaseStore";
import { useInvalidateMembershipQuery } from "../useMembershipQuery";
const EmTitle = styled("em")((param)=>{
    let { theme  } = param;
    return {
        color: theme.palette.secondary.main,
        ...theme.typography.h6
    };
});
const EmContent = styled("em")((param)=>{
    let { theme  } = param;
    return {
        color: theme.palette.text.primary,
        ...theme.typography.subtitle1
    };
});
const UNLIMITED_COUNT = 999999;
export const useLegacyPassLogic = (param)=>{
    let { product , skipConfirmation =false  } = param;
    const router = useRouter();
    const { openModal , closeModal , reset , selectedPass , setFlags  } = usePurchaseStore((state)=>({
            openModal: state.openModal,
            closeModal: state.closeModal,
            reset: state.reset,
            selectedPass: state.selectedPass,
            setFlags: state.setFlags,
            showCouponModal: state.showCouponModal
        }));
    const isProcessingRef = useRef(false);
    const usePassMutation = useMutation(postUseMembership);
    const useMembershipAvailableMutation = useMutation(getMembershipAvailable);
    const { refetch: refetchRelatedProducts  } = useQuery([
        "relatedProduct",
        product.id
    ], ()=>getRelatedProducts(product.id.toString()));
    const invalidateMembershipQuery = useInvalidateMembershipQuery();
    const { closePassSectionModal , updateMembershipAvailable , purchaseProductsInfo , hasAlreadyOwned , selectedPurchaseOption  } = useHandoutProductStore((state)=>({
            closePassSectionModal: state.closePassSectionModal,
            updateMembershipAvailable: state.updateMembershipAvailable,
            purchaseProductsInfo: state.purchaseProductsInfo,
            hasAlreadyOwned: state.hasAlreadyOwned,
            selectedPurchaseOption: state.selectedPurchaseOption
        }));
    const getModalContent = useCallback(()=>{
        const isAmount = selectedPass.raw.productInfo.deductionMethod === "amount";
        return {
            title: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "자료 ",
                    /*#__PURE__*/ _jsxs(EmTitle, {
                        children: [
                            purchaseProductsInfo.totalCount,
                            "개"
                        ]
                    }),
                    "에 이용권을 사용할까요?"
                ]
            }),
            content: isAmount ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "구매 시 이용권에서",
                    " ",
                    /*#__PURE__*/ _jsxs(EmContent, {
                        children: [
                            priceFormatter(purchaseProductsInfo.totalPrice),
                            "원"
                        ]
                    }),
                    "를 차감해요."
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "사용 시 이용권에서 ",
                    /*#__PURE__*/ _jsxs(EmContent, {
                        children: [
                            purchaseProductsInfo.totalCount,
                            "회"
                        ]
                    }),
                    "를 차감해요."
                ]
            })
        };
    }, [
        selectedPass,
        purchaseProductsInfo
    ]);
    const showExceededLimitModal = useCallback((isAmount)=>{
        openModal({
            title: isAmount ? "이용권 사용금액을 초과했어요." : "이용권 사용횟수를 초과했어요.",
            content: isAmount ? "선택하신 자료의 금액이 이용권의 남은 금액을 초과했어요." : "선택하신 상품의 갯수가 이용권의 남은 횟수를 초과했어요.",
            severity: "caution",
            prefix: null,
            suffix: {
                variant: "solid",
                label: "확인",
                handleClick: ()=>{
                    closeModal();
                    mixpanelEvent("Pass Unavailable", {
                        "Popup Title": isAmount ? "이용권 사용금액을 초과했어요." : "이용권 사용횟수를 초과했어요.",
                        "Button Name": "확인",
                        "Button URL": null
                    });
                }
            }
        });
    }, [
        openModal,
        closeModal
    ]);
    const validateDeductionLimits = useCallback(()=>{
        if (selectedPass.raw.productInfo.category !== "freepass") {
            const isAmount = selectedPass.raw.productInfo.deductionMethod === "amount";
            if (isAmount && selectedPass.raw.remainAmount < purchaseProductsInfo.totalPrice) {
                showExceededLimitModal(true);
                return false;
            } else if (!isAmount && selectedPass.raw.remainCount < purchaseProductsInfo.totalCount && selectedPass.raw.productInfo.availableCount !== UNLIMITED_COUNT) {
                showExceededLimitModal(false);
                return false;
            }
        }
        return true;
    }, [
        selectedPass,
        purchaseProductsInfo,
        showExceededLimitModal
    ]);
    // 연관자료 없이 현재 상품만 사용하려는 경우
    const currentProductUsePass = useCallback(async ()=>{
        if (hasAlreadyOwned) {
            openModal({
                title: "이미 이용 중이에요.",
                content: "해당 자료는 자료 보관함에서 확인할 수 있어요.",
                suffix: {
                    variant: "solid",
                    label: "자료 보관함 이동",
                    handleClick: ()=>{
                        var _product_source;
                        mixpanelEvent("Popup Button Clicked", {
                            //팝업 mx-9
                            "Popup Type": "Already Owned Product",
                            "Popup Title": "이미 이용 중이에요.",
                            "Button Name": "자료 보관함 이동",
                            "Button URL": LIBRARIES_INDEX_PAGE
                        });
                        var _product_source_subject;
                        router.push("".concat(LIBRARIES_INDEX_PAGE, "?subject=").concat((_product_source_subject = (_product_source = product.source) === null || _product_source === void 0 ? void 0 : _product_source.subject) !== null && _product_source_subject !== void 0 ? _product_source_subject : Subjects.EN));
                    }
                }
            });
            return;
        }
        if (!validateDeductionLimits()) return;
        // skipConfirmation이 true인 경우, 모달을 띄우지 않고 바로 사용처리
        if (skipConfirmation) {
            usePassConfirmed();
            return;
        }
        const { title , content  } = getModalContent();
        openModal({
            title,
            content,
            prefix: {
                variant: "soft",
                label: "취소",
                handleClick: ()=>{
                    closeModal();
                }
            },
            suffix: {
                variant: "solid",
                label: "이용권 사용하기",
                handleClick: ()=>{
                    usePassConfirmed();
                }
            }
        });
    }, [
        hasAlreadyOwned,
        openModal,
        router,
        product,
        closeModal,
        getModalContent,
        validateDeductionLimits,
        skipConfirmation
    ]);
    const relatedProductUsePass = useCallback(async ()=>{
        if (!validateDeductionLimits()) return;
        const productIds = purchaseProductsInfo.selectedRelatedProducts.map((p)=>p.id.toString());
        const res = await useMembershipAvailableMutation.mutateAsync({
            membershipId: selectedPass.id.toString(),
            productIds
        });
        const hasUnusableProduct = res.data.filter((p)=>!p.usable).map((p)=>p.productId.toString());
        if (hasAlreadyOwned) {
            if (hasUnusableProduct.length > 0) {
                updateMembershipAvailable(hasUnusableProduct);
                setFlags({
                    showAvailableRelatedProductsModal: true
                });
                closePassSectionModal();
                return;
            }
            updateMembershipAvailable([]);
            setFlags({
                showAvailableRelatedProductsModal: true
            });
            closePassSectionModal();
            return;
        }
        if (hasUnusableProduct.length > 0) {
            updateMembershipAvailable(hasUnusableProduct);
            setFlags({
                showAvailableRelatedProductsModal: true
            });
            closePassSectionModal();
            return;
        }
        if (skipConfirmation) {
            usePassConfirmed();
            return;
        }
        const { title , content  } = getModalContent();
        openModal({
            title,
            content,
            prefix: {
                variant: "soft",
                label: "취소",
                handleClick: ()=>{
                    closeModal();
                }
            },
            suffix: {
                variant: "solid",
                label: "이용권 사용하기",
                handleClick: ()=>{
                    usePassConfirmed();
                }
            }
        });
    }, [
        purchaseProductsInfo,
        useMembershipAvailableMutation,
        updateMembershipAvailable,
        hasAlreadyOwned,
        openModal,
        closeModal,
        closePassSectionModal,
        selectedPass,
        setFlags,
        getModalContent,
        validateDeductionLimits,
        skipConfirmation
    ]);
    const handleUsePass = useCallback(async ()=>{
        // mx-3,18
        mixpanelEvent("Purchase Button Clicked", {
            PurchaseType: "이용권 사용",
            "Pass ID": selectedPass.id,
            "Pass Title": selectedPass.raw.productInfo.title,
            "Product IDs": purchaseProductsInfo.selectedRelatedProducts.map((p)=>p.id).concat([
                product.id.toString()
            ]),
            "Product Titles": purchaseProductsInfo.selectedRelatedProducts.map((p)=>p.title).concat([
                product.title
            ]),
            "Purchase option": selectedPurchaseOption
        });
        if (purchaseProductsInfo.selectedRelatedProducts.length === 0) {
            currentProductUsePass();
        } else {
            relatedProductUsePass();
        }
    }, [
        selectedPass,
        purchaseProductsInfo,
        product,
        currentProductUsePass,
        relatedProductUsePass,
        selectedPurchaseOption
    ]);
    // 이용권 사용하기 버튼 클릭 후
    const usePassConfirmed = useCallback(()=>{
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;
        const productIds = hasAlreadyOwned ? purchaseProductsInfo.selectedRelatedProducts.map((p)=>p.id.toString()) : purchaseProductsInfo.selectedRelatedProducts.map((p)=>p.id.toString()).concat(product.id.toString());
        usePassMutation.mutate({
            productIds,
            membershipId: selectedPass.id.toString(),
            choicePurchaseOption: selectedPurchaseOption
        }, {
            onSuccess: async (data)=>{
                isProcessingRef.current = false;
                invalidateMembershipQuery(product.id);
                openModal({
                    title: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "자료 보관함에 자료 ",
                            /*#__PURE__*/ _jsxs(EmTitle, {
                                children: [
                                    productIds.length,
                                    "개"
                                ]
                            }),
                            "를 추가했어요."
                        ]
                    }),
                    content: "내 자료들은 자료 보관함에서 확인할 수 있어요.",
                    severity: "success",
                    suffix: {
                        variant: "solid",
                        label: "자료 보관함 가기",
                        handleClick: ()=>{
                            var _product_source;
                            //mx-9
                            mixpanelEvent("Popup Button Clicked", {
                                "Popup Type": "Use Pass",
                                "Popup Title": "자료 보관함에 자료를 추가했어요.",
                                "Button Name": "자료 보관함 이동",
                                "Button URL": LIBRARIES_INDEX_PAGE
                            });
                            var _product_source_subject;
                            router.push("".concat(LIBRARIES_INDEX_PAGE, "?subject=").concat((_product_source_subject = (_product_source = product.source) === null || _product_source === void 0 ? void 0 : _product_source.subject) !== null && _product_source_subject !== void 0 ? _product_source_subject : Subjects.EN));
                        }
                    },
                    prefix: {
                        variant: "soft",
                        label: "닫기",
                        handleClick: ()=>{
                            closeModal();
                            mixpanelEvent("Popup Button Clicked", {
                                "Popup Type": "Use Pass",
                                "Popup Title": "자료 보관함에 자료를 추가했어요.",
                                "Button Name": "닫기",
                                "Button URL": null
                            });
                        }
                    }
                });
                await refetchRelatedProducts();
                // mx-15
                mixpanelEvent("Pass Use Completed", {
                    "Pass ID": selectedPass.id,
                    "Pass Category": selectedPass.raw.productInfo.category,
                    "Pass Category Title": selectedPass.raw.productInfo.categoryTitle,
                    "Pass Title": selectedPass.raw.productInfo.title,
                    "Product ID": product.id,
                    "Product Category": product.category,
                    "Product Title": product.title,
                    "Product Price": product.price,
                    "Author ID": product.author,
                    "Author Name": selectedPass.raw.productInfo.category,
                    "Purchase option": selectedPurchaseOption
                });
            },
            onError: async (error)=>{
                isProcessingRef.current = false;
                if (error.response.status === 409 && error.response.data.message.includes("could not purchase or use the product")) {
                    openModal({
                        title: "해당 상품은 판매가 중단됐어요.",
                        content: "저작권 이용기간이 만료된 상품이거나 판매가 중지된 상품이에요.",
                        severity: "caution",
                        suffix: null
                    });
                }
                if (error.response.status === 409 && error.response.data.message.includes("no remains")) {
                    const isAmount = selectedPass.raw.productInfo.deductionMethod === "amount";
                    showExceededLimitModal(isAmount);
                    return;
                }
                if (error.response.status === 409 && error.response.data.message.includes("user already has a product")) {
                    openModal({
                        title: "이미 이용중인 자료가 있어요",
                        content: "해당 자료를 제외한 자료만 이용권 차감돼요",
                        severity: "caution",
                        suffix: {
                            variant: "solid",
                            label: "자료 보관함 바로가기",
                            handleClick: ()=>{
                                router.push(LIBRARIES_INDEX_PAGE);
                            }
                        }
                    });
                    await refetchRelatedProducts();
                }
                if (error.response.status === 403) {
                    openModal({
                        title: "이용권을 구매해주세요!",
                        content: "해당 자료는 이용권으로 결제할 수 있어요.",
                        severity: "caution",
                        suffix: {
                            variant: "solid",
                            label: "이용권 구매하기",
                            handleClick: ()=>{
                                router.push(PASSES_ORDER);
                            }
                        }
                    });
                }
                // solvookplus, solvookplus-subscribe,쏠플의 경우 이용권으로 다운로드 사용 불가에 대한 에러
                if (error.response.status === 422) {
                    openModal({
                        title: "이용권의 사용 범위와 맞지 않는 자료에요.",
                        content: "마이페이지 > 이용권 관리에서 선택한 이용권의 사용 범위를 확인해주세요.",
                        severity: "caution",
                        suffix: {
                            variant: "solid",
                            label: "확인",
                            handleClick: ()=>{
                                mixpanelEvent("Popup Button Clicked", {
                                    "Popup Type": "Pass Use Failed",
                                    "Popup Title": "이용권의 사용 범위와 맞지 않는 자료에요.",
                                    "Button Name": "확인",
                                    "Button URL": null
                                });
                                closeModal();
                            }
                        },
                        prefix: {
                            variant: "soft",
                            label: "이용권 관리 가기",
                            handleClick: ()=>{
                                mixpanelEvent("Popup Button Clicked", {
                                    "Popup Type": "Pass Use Failed",
                                    "Popup Title": "이용권의 사용 범위와 맞지 않는 자료에요.",
                                    "Button Name": "이용권 관리 가기",
                                    "Button URL": MYPAGE_PASSES
                                });
                                router.push(MYPAGE_PASSES);
                            }
                        }
                    });
                }
            }
        });
    }, [
        selectedPass,
        purchaseProductsInfo,
        product,
        usePassMutation,
        refetchRelatedProducts,
        router,
        hasAlreadyOwned,
        openModal,
        closeModal,
        showExceededLimitModal,
        selectedPurchaseOption
    ]);
    useEffect(()=>{
        return ()=>{
            reset();
        };
    }, []);
    return {
        // 이용권 사용 프로세스 시작하는 메서드. 단일 상품 + 연관 상품에 대한 로직 처리
        handleUsePass,
        // 사용을 확정하는 메서드. 사용 요청을 보내고 결과에 따른 모달 처리
        usePassConfirmed
    };
};
